// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
import { format } from "d3-format";
import { UNIT_TYPES, MISSING_VALUE } from "./consts.js";

/** @typedef {import("./types.d.ts").UnitType} UnitType */
/** @typedef {import("./types.d.ts").MetricMeta} MetricMeta */
/** @typedef {import("./types.d.ts").DisplayFormat} DisplayFormat */

/** @type {Record<string, { value: string, axis: string, change: string }>} */
const FORMAT_PRESETS = {
  dollars_cents: { value: "$,.2f", axis: "$,.2f", change: ".1%" },
  dollars_ones: { value: "$,.0f", axis: "$.2s", change: ".1%" },
  dollars_tens: { value: "$,.0f", axis: "$.2s", change: ".0%" },
  dollars_hundreds: { value: "$,.0f", axis: "$.2s", change: ".0%" },
  dollars_thousands: { value: "$,.0f", axis: "$.3s", change: ".0%" },
  percent_0: { value: ".0%", axis: ".0%", change: ".0%" },
  percent_1: { value: ".1%", axis: ".1%", change: ".1%" },
  integer: { value: ",.0f", axis: ",.0f", change: ".1%" }
};

/** @type {Record<string, number>} */
const ROUND_TO = {
  dollars_cents: 0.01,
  dollars_ones: 1,
  dollars_tens: 10,
  dollars_hundreds: 100,
  dollars_thousands: 1000
};

/** @type {Record<string, string>} */
const FORMATS = {
  [UNIT_TYPES.DOLLARS]: "$,.3r",
  [UNIT_TYPES.CREDIT_SCORE]: ",.0~f",
  [UNIT_TYPES.PERCENT]: ".3p",
  [UNIT_TYPES.RATE_CHANGE]: ".1%"
};

/** @type {Record<string, string>} */
const AXIS_FORMATS = {
  [UNIT_TYPES.DOLLARS]: "$.2s",
  [UNIT_TYPES.CREDIT_SCORE]: ",.0f",
  [UNIT_TYPES.PERCENT]: ".0%",
  [UNIT_TYPES.RATE_CHANGE]: ".1%"
};

/**
 * Pre-round a value to the precision level specified by a display format preset.
 * @param {number} value
 * @param {string} displayFormat
 * @returns {number}
 */
function preRound(value, displayFormat) {
  const step = ROUND_TO[displayFormat];
  if (step == null) return value;
  return Math.round(value / step) * step;
}

/**
 * @param {number | null} value
 * @param {string} fmtString
 * @returns {string}
 */
function applyFormat(value, fmtString) {
  if (value === null || value === undefined || isNaN(value)) {
    return MISSING_VALUE;
  }
  return format(fmtString)(value);
}

/**
 * Format a value for display. Accepts metric metadata for per-metric precision,
 * or falls back to unit-type defaults when metadata is not available.
 * @param {number | null} value
 * @param {MetricMeta | null | undefined} metricMeta
 * @returns {string}
 */
export function formatValue(value, metricMeta) {
  if (value === null || value === undefined || isNaN(value)) {
    return MISSING_VALUE;
  }
  const preset = metricMeta?.display_format && FORMAT_PRESETS[metricMeta.display_format];
  if (preset) {
    return applyFormat(preRound(value, metricMeta.display_format), preset.value);
  }
  return applyFormat(value, FORMATS[metricMeta?.units] || ",.2f");
}

/**
 * Format a value for axis/legend display. Accepts metric metadata for per-metric precision,
 * or falls back to unit-type defaults when metadata is not available.
 * @param {number | null} value
 * @param {MetricMeta | null | undefined} metricMeta
 * @returns {string}
 */
export function formatShortValue(value, metricMeta) {
  if (value === null || value === undefined || isNaN(value)) {
    return MISSING_VALUE;
  }
  const preset = metricMeta?.display_format && FORMAT_PRESETS[metricMeta.display_format];
  if (preset) {
    return applyFormat(preRound(value, metricMeta.display_format), preset.axis);
  }
  return applyFormat(value, AXIS_FORMATS[metricMeta?.units] || ",.2f");
}

/**
 * Format a rate-of-change value. Accepts metric metadata for per-metric precision,
 * or falls back to the default ".1%".
 * @param {number | null} value
 * @param {MetricMeta | null | undefined} metricMeta
 * @returns {string}
 */
export function formatChange(value, metricMeta) {
  if (value === null || value === undefined || isNaN(value)) {
    return MISSING_VALUE;
  }
  const preset = metricMeta?.display_format && FORMAT_PRESETS[metricMeta.display_format];
  if (preset) return applyFormat(value, preset.change);
  return applyFormat(value, FORMATS[UNIT_TYPES.RATE_CHANGE] || ".1%");
}

/**
 * Round a value to the same precision the user sees on screen.
 * Returns a number (not a string). Available for computing percentage change
 * from values that match what is displayed.
 * @param {number | null} value
 * @param {MetricMeta | null | undefined} metricMeta
 * @returns {number | null}
 */
export function roundForDisplay(value, metricMeta) {
  if (value === null || value === undefined || isNaN(value)) return null;
  const preset = metricMeta?.display_format && FORMAT_PRESETS[metricMeta.display_format];
  if (!preset) {
    // Fallback: format then parse for legacy unit-type behavior
    const formatted = formatValue(value, metricMeta);
    const cleaned = formatted.replace(/[$,%]/g, "").trim();
    return Number(cleaned) || null;
  }
  return preRound(value, metricMeta.display_format);
}

/**
 * @param {string} inputText
 * @returns {string}
 */
export function sentenceCase(inputText) {
  if (!inputText) return "";
  return inputText.charAt(0).toUpperCase() + inputText.slice(1);
}
